import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import EditHistoryLink from './EditHistoryLink';
import { SelectField, MultiSelectField, TextField, normalizeOptions } from './fields';
import type { CustomDrawerProps } from './TablemaintPage';
import BillToLookupField, { type BillToValue } from '../BillToLookupField';
import { ORDER_MOVE_TYPES } from '../../constants/orderFieldOptions';
import { titleCase } from '../../utils/titleCase';
import {
  createInvoiceSetup, updateInvoiceSetup, getEdiOrderHandles,
} from '../../services/ediinvoicesetup.service';

/**
 * Bespoke create/edit drawer for EDI Invoicing Setup (cms.edi_invoicehistoryrecno).
 * PK is billto (one setup per Bill To):
 *  - CREATE: pick a Bill To (enriched lookup) + the required Starting Invoice # (masterpro,
 *    translated server-side to invoicehistoryrecno) + optional Required EDI Handle
 *    (dropdown of edi_orders handles) + Move Type List (multiselect).
 *  - EDIT: Bill To is locked (it's the key); the rest are editable.
 * Saves via the billto-keyed /edi-invoice-setup router. Replaces V1 editedi_invoicehistoryrecno.
 */
const TABLE = 'edi-invoice-setup';
const s = (v: any): string => (v == null ? '' : String(v));

const EdiInvoiceSetupEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isEdit = !!row;
  const billtoRecno = Number(row?.billto) || 0;

  const [billto, setBillto] = useState<BillToValue>({ recno: billtoRecno, name: s(row?.cname) });
  const [masterpro, setMasterpro] = useState(s(row?.masterpro));
  const [edihandle, setEdihandle] = useState(s(row?.edihandle204));
  const [moveTypes, setMoveTypes] = useState<string[]>(
    s(row?.invmovetype) ? s(row?.invmovetype).split(',').map((x) => x.trim()).filter(Boolean) : [],
  );
  const [handleOpts, setHandleOpts] = useState<{ value: string; label: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => { getEdiOrderHandles().then(setHandleOpts).catch(() => setHandleOpts([])); }, []);

  // Keep a stored handle selectable even if it's no longer in the edi_orders list.
  const handleOptions = useMemo(() => {
    const base = [{ value: '', label: '— None —' }, ...handleOpts];
    if (edihandle && !base.some((o) => o.value === edihandle)) return [...base, { value: edihandle, label: edihandle }];
    return base;
  }, [handleOpts, edihandle]);

  const moveOptions = useMemo(() => normalizeOptions(ORDER_MOVE_TYPES.map((m) => ({ value: m, label: titleCase(m) }))), []);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!isEdit && !billto.recno) e.push('Bill To is required.');
    if (!masterpro.trim()) e.push('Starting Invoice # is required.');
    return e;
  }, [isEdit, billto.recno, masterpro]);

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isEdit) {
        await updateInvoiceSetup(billtoRecno, { masterpro, edihandle204: edihandle, invmovetype: moveTypes });
      } else {
        await createInvoiceSetup({ billto: billto.recno, masterpro, edihandle204: edihandle, invmovetype: moveTypes });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save EDI invoicing setup.');
      setIsSaving(false);
    }
  }, [errors, isEdit, billtoRecno, billto.recno, masterpro, edihandle, moveTypes, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isEdit ? 'Save Changes' : 'Create Setup'}</>)}
      </button>
    </div>
  );

  const title = isEdit ? `EDI Invoicing Setup — ${s(row?.cname) || `Bill To #${billtoRecno}`}` : 'Add EDI Invoicing Setup';

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="44rem" footer={footer}>
      <div className="space-y-5">
        {isEdit && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={billtoRecno} />
          </div>
        )}

        {showErrors && errors.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}

        {/* Bill To — enriched lookup on create; locked (it's the key) on edit. */}
        {isEdit ? (
          <div className="flex flex-col">
            <span className="text-xs font-medium text-muted mb-1">Bill To</span>
            <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
              {s(row?.cname) || '—'} <span className="text-gray-400">#{billtoRecno}</span>
            </div>
          </div>
        ) : (
          <BillToLookupField value={billto} onChange={setBillto} disabled={isSaving} required />
        )}

        <TextField
          label="Starting Invoice #"
          value={masterpro}
          onChange={setMasterpro}
          disabled={isSaving}
          required
          invalid={showErrors && !masterpro.trim()}
        />

        <SelectField
          label="Required EDI Handle"
          value={edihandle}
          onChange={setEdihandle}
          options={handleOptions}
          disabled={isSaving}
        />

        <MultiSelectField
          label="Move Type List"
          value={moveTypes}
          onChange={setMoveTypes}
          options={moveOptions}
          disabled={isSaving}
          placeholder="Select move types…"
        />
      </div>
    </Drawer>
  );
};

export default EdiInvoiceSetupEditDrawer;
